import React, { useState } from 'react';
import {
    checkIfKnownIbanBceCombination,
    formatNationalRegister,
    formatNumberAddress,
    formatPhone,
    handleDeleteFile,
    handleFileValidation,
    isValidNationalRegisterNumber,
} from './FormHelper';
import AddressPartialForm from './PartialForm/AddressPartialForm';
import ContactPartialForm from './PartialForm/ContactPartialForm';
import { IbanOwnerType } from './Types/IbanOwnerType';
import { useTranslation } from '../../Helpers';
import { WidgetHelper } from '../WidgetHelper';
import { FloatLabel } from '../../Components';
import { validateJeuneForm } from './FormValidator';
import { PrimeJeunesService } from '../../Services';
import { PrimeSuccessWidget } from './PrimeSuccessWidget';
import { IJeuneFormData } from './Types/IJeuneFormData';
import { IbanOwnerSelector } from './PartialForm/IbanOwnerSelector';
import AddressLegalRepresentativePartialForm from './PartialForm/AddressLegalRepresentativePartialForm';
import CheckboxField from './PartialForm/CheckboxField';
import FileUploadField from './PartialForm/FileUploadField';

interface Props {
    language: string;
    translationJson: string;
}

const PrimeJeuneForm = ({ translationJson }: Props) => {
    const [data, setData] = useState<IJeuneFormData>({
        addressLocality: '',
        addressLocalityLegalRepresentative: '',
        addressNumber: '',
        addressNumberLegalRepresentative: '',
        addressPostal: '',
        addressPostalLegalRepresentative: '',
        addressStreet: '',
        addressStreetLegalRepresentative: '',
        bic: '',
        denominationCompany: '',
        iban: '',
        ibanOwner: IbanOwnerType.OwnerAsker,
        isIbanOk: false,
        isKnownCombinationCompanyIBAN: false,
        legalRepresentantName: '',
        mail: '',
        mailConfirmation: '',
        name: '',
        nameLegalRepresentative: '',
        niss: '',
        nissIsValid: false,
        numberCompany: '',
        phone: '',
        phoneConfirmation: '',
        socialCompany: '',
        surname: '',
        surnameLegalRepresentative: '',
        schoolAttestationFile: null as any,
        bankAttestationFile: null as any,
        dataAreCorrect: false,
    });

    const [errors, setErrors] = useState<
        Partial<Record<keyof IJeuneFormData, string>>
    >({});
    const [isSubmit, setIsSubmit] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);

    const translations = useTranslation(translationJson) as Record<
        string,
        string
    >;

    const borderRadius = {
        borderRadius: '5px',
    } as React.CSSProperties;

    const handleChange = <K extends keyof IJeuneFormData>(
        field: K,
        value: IJeuneFormData[K]
    ) => {
        setData((prev: any) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleBlurNumberCompany = async () => {
        const isIbanOk = data.iban.length >= 16;

        if (data.iban && data.numberCompany && isIbanOk) {
            const isKnown = await checkIfKnownIbanBceCombination(
                data.iban,
                data.numberCompany
            );

            setData((prev: any) => ({
                ...prev,
                isIbanOk,
                isKnownCombinationCompanyIBAN: isKnown,
            }));
        } else {
            setData((prev: any) => ({
                ...prev,
                isIbanOk,
                isKnownCombinationCompanyIBAN: false,
            }));
        }
    };

    const handleBlurNumberCompanyVoid = () => {
        void handleBlurNumberCompany();
    };

    const handleChangeNumberAddress = (value: string) => {
        const formatted = formatNumberAddress(value);

        setData((prev: any) => ({
            ...prev,
            addressNumber: formatted,
        }));
    };

    const handleChangeNumberAddressRepresentative = (value: string) => {
        const formatted = formatNumberAddress(value);

        setData((prev: any) => ({
            ...prev,
            addressNumberLegalRepresentative: formatted,
        }));
    };

    const handleChangePhone = (value: string, field: string) => {
        const phoneFormatted = formatPhone(value);

        setData((prev: any) => ({
            ...prev,
            [field]: phoneFormatted,
        }));
    };

    const handleChangeNationalRegisterNumber = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const rawValue = event.target.value;
        const cleanValue = formatNationalRegister(rawValue);
        const isValid = isValidNationalRegisterNumber(cleanValue);

        setData((prev) => ({
            ...prev,
            niss: cleanValue,
            nissIsValid: isValid,
        }));
    };

    const handleChangeFiles = <K extends keyof IJeuneFormData>(
        e: React.ChangeEvent<HTMLInputElement>,
        name: K
    ) => {
        if (e.target.files && e.target.files[0]) {
            handleFileValidation<IJeuneFormData>(e.target.files[0], {
                fieldName: name,
                setData,
            });
        }
    };

    const onFileDelete = <K extends keyof IJeuneFormData>(name: K) => {
        handleDeleteFile<IJeuneFormData>(name, setData);
    };

    const handleIbanOwnerChange = (owner: IbanOwnerType, name?: string) => {
        setData((prev) => ({
            ...prev,
            ibanOwner: owner,
            legalRepresentantName: name ?? '',
        }));
    };

    const handleSubmit = () => {
        void handleSubmitAsync();
    };

    const handleSubmitAsync = async () => {
        const validationErrors = validateJeuneForm(data, translations);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length !== 0) {
            console.warn('FORM BLOCKED BY VALIDATION', validationErrors);
            return;
        }

        try {
            if (typeof window.getCaptchaToken !== 'function') {
                console.error('getCaptchaToken is not available');
                throw new Error('Captcha is not available');
            }

            const captchaToken = await window.getCaptchaToken(
                'prime_jeune_request'
            );

            if (!captchaToken) {
                throw new Error('Captcha token is empty');
            }

            const response = await PrimeJeunesService.pushData(
                data,
                captchaToken
            );

            if (
                response &&
                response.status === 200 &&
                (await response.json()) === true
            ) {
                setIsSubmit(true);
                setIsSuccess(true);
            } else {
                setIsSubmit(true);
                setIsSuccess(false);
            }
        } catch (error) {
            console.error('Erreur lors de la soumission:', error);
            setIsSubmit(true);
            setIsSuccess(false);
        }
    };
    if (isSubmit) {
        return (
            <PrimeSuccessWidget
                isSuccess={isSuccess}
                dictSuccessText1={translations['Site.Prime.SuccessTextFpie1']}
                dictSuccessText2=""
                dictSuccessContact1={
                    translations['Site.Prime.SuccessContactEmployeur']
                }
                dictSuccessContact2=""
                dictFailLabel=""
                dictSuccessPhone=""
                dictSuccessSignature1=""
                dictSuccessSignature2=""
            />
        );
    }

    return (
        <form
            id="primeForm"
            data-skip-captcha="true"
            onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
            }}
        >
            <div className="row">
                <div className="col-12">
                    <h2 className="h2">
                        {translations['Site.Prime.TitleCompany']}
                    </h2>
                </div>
            </div>

            <div className="row">
                <div className="col-8">
                    <FloatLabel
                        active={!!data.name}
                        label={translations['Site.Prime.Name']}
                        labelId="name"
                    >
                        <input
                            type="text"
                            name="name"
                            value={data.name}
                            onChange={(e) =>
                                handleChange('name', e.target.value)
                            }
                        />
                    </FloatLabel>

                    {errors.name && (
                        <div className="row">
                            <label className="alert-danger m-auto">
                                {translations['Site.Prime.ErrorSubmit']}
                            </label>
                        </div>
                    )}
                </div>

                <div className="col-8">
                    <FloatLabel
                        active={!!data.surname}
                        label={translations['Site.Prime.Surname']}
                        labelId="surname"
                    >
                        <input
                            type="text"
                            name="surname"
                            value={data.surname}
                            onChange={(e) =>
                                handleChange('surname', e.target.value)
                            }
                        />
                    </FloatLabel>

                    {errors.surname && (
                        <div className="row">
                            <label className="alert-danger m-auto">
                                {translations['Site.Prime.ErrorSubmit']}
                            </label>
                        </div>
                    )}
                </div>

                <div className="col-8">
                    <FloatLabel
                        active={!!data.niss}
                        label={
                            translations['Site.Prime.NationalRegisterApplicant']
                        }
                        labelId="nationalRegisterApplicant"
                    >
                        <input
                            type="text"
                            name="nationalRegisterApplicant"
                            value={data.niss}
                            onChange={handleChangeNationalRegisterNumber}
                        />
                    </FloatLabel>

                    {errors.niss && (
                        <div className="row">
                            <label className="alert-danger m-auto">
                                {translations['Site.Prime.ErrorSubmit']}
                            </label>
                        </div>
                    )}
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <h2 className="h2">{translations['Site.Prime.Address']}</h2>
                </div>
            </div>

            <AddressPartialForm
                values={data}
                onChange={handleChange}
                onChangeNumberAddress={handleChangeNumberAddress}
                errors={errors}
                dictAddressStreetLabel={
                    translations['Site.Prime.AddressStreet']
                }
                dictAddressNumberLabel={
                    translations['Site.Prime.AddressNumber']
                }
                dictAddressPostalLabel={
                    translations['Site.Prime.AddressPostal']
                }
                dictAddressLocalityLabel={
                    translations['Site.Prime.AddressLocality']
                }
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
            />

            <ContactPartialForm
                values={data}
                onChange={handleChange}
                onChangePhone={handleChangePhone}
                onIbanCheck={handleBlurNumberCompanyVoid}
                errors={errors}
                dictMailLabel={translations['Site.Prime.MailCompany']}
                dictMailConfirmationLabel={
                    translations['Site.Prime.MailCompanyConfirmation']
                }
                dictPhoneLabel={translations['Site.Prime.PhoneCompany']}
                dictPhoneConfirmationLabel={
                    translations['Site.Prime.PhoneConfirmation']
                }
                dictIbanCompanyLabel={translations['Site.Prime.IbanCompany']}
                dictBicCompanyLabel={translations['Site.Prime.BicCompany']}
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                dictMailConfirmationError={
                    translations['Site.Prime.MailCompanyConfirmationError']
                }
                dictPhoneConfirmationError={
                    translations['Site.Prime.PhoneConfirmationError']
                }
                dictIdentificationCodeError={
                    translations['Site.Prime.IdentificationCodeError']
                }
                isIbanOk={data.iban.length >= 16}
                isBelgianIban={data.iban.startsWith('BE')}
            />

            <div className="row">
                <div className="col-12 mt-5">
                    <IbanOwnerSelector
                        values={data}
                        initialOwner={data.ibanOwner}
                        initialRepresentantName={data.legalRepresentantName}
                        dictIbanOwnerAsker={
                            translations['Site.Prime.IbanOwnerAsker']
                        }
                        dictIbanOwnerRepresentant={
                            translations['Site.Prime.IbanOwnerRepresentant']
                        }
                        dictRepresentantNameLabel={
                            translations['Site.Prime.RepresentantName']
                        }
                        dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                        onChange={handleIbanOwnerChange}
                        errors={errors}
                    />
                </div>
            </div>

            <AddressLegalRepresentativePartialForm
                values={data}
                onChange={handleChange}
                onChangeNumberAddress={handleChangeNumberAddressRepresentative}
                errors={errors}
                dictAddressStreetLabel={
                    translations['Site.Prime.AddressStreetLegalRepresentative']
                }
                dictAddressNumberLabel={
                    translations['Site.Prime.AddressNumberLegalRepresentative']
                }
                dictAddressPostalLabel={
                    translations['Site.Prime.AddressPostalLegalRepresentative']
                }
                dictAddressLocalityLabel={
                    translations[
                        'Site.Prime.AddressLocalityLegalRepresentative'
                    ]
                }
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                dictNameLegalRepresentativeLabel={
                    translations['Site.Prime.NameLegalRepresentative']
                }
                dictSurnameLegalRepresentativeLabel={
                    translations['Site.Prime.SurnameLegalRepresentative']
                }
                dictTitleLegalRepresentativeContactDetails={
                    translations[
                        'Site.Prime.TitleLegalRepresentativeContactDetails'
                    ]
                }
            />

            <div className="col-12">
                <div className="row px-4">
                    <div className="col-12 pb-4 border" style={borderRadius}>
                        <div className="row">
                            <FileUploadField
                                label={
                                    translations[
                                        'Site.Prime.SchoolAttestationFileJeunes'
                                    ]
                                }
                                infoLabel={
                                    translations[
                                        'Site.Prime.SchoolAttestationFileInfo'
                                    ]
                                }
                                file={data.schoolAttestationFile}
                                noneLabel={translations['Site.Prime.NoneLabel']}
                                uploadLabel={translations['Site.Prime.Upload']}
                                error={
                                    errors.schoolAttestationFile &&
                                    translations['Site.Prime.ErrorSubmit']
                                }
                                fieldName="schoolAttestationFile"
                                onFileSelect={handleChangeFiles}
                                onFileDelete={onFileDelete}
                            />
                        </div>

                        <div className="row">
                            <FileUploadField
                                label={
                                    translations[
                                        'Site.Prime.BankAttestationFile'
                                    ]
                                }
                                infoLabel={
                                    translations[
                                        'Site.Prime.BankAttestationFileInfoJeunes'
                                    ]
                                }
                                file={data.bankAttestationFile}
                                noneLabel={translations['Site.Prime.NoneLabel']}
                                uploadLabel={translations['Site.Prime.Upload']}
                                error={errors.bankAttestationFile}
                                errorLabel={
                                    translations['Site.Prime.ErrorSubmit']
                                }
                                fieldName="bankAttestationFile"
                                onFileSelect={handleChangeFiles}
                                onFileDelete={onFileDelete}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <CheckboxField
                id="dataAreCorrect"
                name="dataAreCorrect"
                label={translations['Site.Prime.DataAreCorrectLabel']}
                value={data.dataAreCorrect}
                onChange={handleChange}
                error={
                    errors.dataAreCorrect &&
                    translations['Site.Prime.ErrorSubmit']
                }
                style={borderRadius}
            />

            <div className="col-12 text-right pt-3">
                <label className="alert-light">
                    {translations['Site.PrimePhoenix.MandatoryField']}
                </label>
            </div>

            <div className="form-group submit mt-5">
                <button type="submit" className="btn-block">
                    <i className="icon-check"></i>
                    {translations['Site.FondsRebond.Submit']}
                </button>
            </div>
        </form>
    );
};

WidgetHelper.register(PrimeJeuneForm, 'prime-jeune-form-widget');
